// Collection and search pages: filters, quick chips and sort without reloads, "Show more lamps", back button support.
const root = document.querySelector("[data-am-plp]");

if (root) {
  const sectionId = root.dataset.section;
  const parser = new DOMParser();
  let controller;
  let debounce;

  const facetsForm = () => document.querySelector("[data-am-facets]");

  function paramsFromForm() {
    const form = facetsForm();
    const params = new URLSearchParams();
    if (!form) return params;
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== "") params.append(key, value);
    }
    // Keep URLs clean: the first sort option is the page's default.
    const defaultSort = form.querySelector('input[name="sort_by"]')?.value;
    if (params.get("sort_by") === defaultSort) params.delete("sort_by");
    return params;
  }

  // Remember which control had focus so re-rendering the filters does not lose the shopper's place.
  function focusKey() {
    const el = document.activeElement;
    if (!el || !el.closest("[data-am-facets], [data-am-quick-chips]")) return null;
    if (el.dataset.amPrice) return { selector: `[data-am-price="${CSS.escape(el.dataset.amPrice)}"]` };
    if (el.closest("[data-am-quick-chips]")) return { selector: "[data-am-quick-chips] a, [data-am-count]" };
    if (!el.name) return null;
    const value = el.type === "checkbox" || el.type === "radio" ? el.value : null;
    return {
      selector: value === null
        ? `[data-am-facets] [name="${CSS.escape(el.name)}"]`
        : `[data-am-facets] [name="${CSS.escape(el.name)}"][value="${CSS.escape(value)}"]`,
    };
  }

  function restoreFocus(key) {
    if (!key) return;
    document.querySelector(key.selector)?.focus({ preventScroll: true });
  }

  function swap(doc, selector) {
    const fresh = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (fresh && current) current.replaceWith(fresh);
  }

  async function render(url, { push = true, key = focusKey() } = {}) {
    controller?.abort();
    controller = new AbortController();
    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set("section_id", sectionId);
    root.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(sectionUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = parser.parseFromString(await response.text(), "text/html");
      const openGroups = [...document.querySelectorAll("[data-am-facets] details")].map((d) => d.open);
      swap(doc, "[data-am-results]");
      swap(doc, "[data-am-count]");
      swap(doc, "[data-am-facets]");
      swap(doc, "[data-am-show-results]");
      swap(doc, "[data-am-active-count]");
      document.querySelectorAll("[data-am-facets] details").forEach((d, i) => {
        if (openGroups[i] !== undefined) d.open = openGroups[i];
      });
      restoreFocus(key);
      if (push) window.history.pushState({ amPlp: true }, "", url);
      const count = document.querySelector("[data-am-count]")?.textContent.trim();
      if (count) window.AM?.announce?.(`${count} shown`);
    } catch (error) {
      if (error.name !== "AbortError") window.location.href = url;
    } finally {
      root.removeAttribute("aria-busy");
    }
  }

  function applyFromForm(key) {
    const params = paramsFromForm();
    params.delete("page");
    const query = params.toString();
    render(`${window.location.pathname}${query ? `?${query}` : ""}`, { key });
  }

  document.addEventListener("change", (event) => {
    if (event.target.closest("[data-am-facets]")) {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(applyFromForm, event.target.type === "number" ? 600 : 80);
      return;
    }
    const sort = event.target.closest("[data-am-sort]");
    if (sort) {
      const radio = document.querySelector(`[data-am-facets] input[name="sort_by"][value="${CSS.escape(sort.value)}"]`);
      if (radio) radio.checked = true;
      applyFromForm({ selector: "[data-am-sort]" });
    }
  });

  document.addEventListener("submit", (event) => {
    if (!event.target.closest("[data-am-facets]")) return;
    event.preventDefault();
    applyFromForm();
  });

  // Price presets (in the filters) and the "Under £50" quick chip set the price inputs, so the other filters stay on.
  function setPrice(min, max) {
    const minInput = document.getElementById("am-price-min");
    const maxInput = document.getElementById("am-price-max");
    if (!minInput || !maxInput) return false;
    minInput.value = min > 0 ? String(min) : "";
    maxInput.value = max > 0 ? String(max) : "";
    return true;
  }

  // Active filter chips, "Clear all", quick chips and price presets re-render in place.
  document.addEventListener("click", (event) => {
    const link = event.target.closest(".am-plp__active a, .am-filters__foot a, .am-quick-chips a, [data-am-price]");
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    // Where focus goes once the results re-render: the same preset, the first quick chip, or the result count.
    let key = { selector: "[data-am-count]" };
    if (link.dataset.amPrice) key = { selector: `[data-am-price="${CSS.escape(link.dataset.amPrice)}"]` };
    else if (link.closest("[data-am-quick-chips]")) key = { selector: "[data-am-quick-chips] a, [data-am-count]" };
    if (link.dataset.amPrice) {
      const [min, max] = link.dataset.amPrice.split(",").map(Number);
      const already = link.getAttribute("aria-current") === "true";
      if (setPrice(already ? 0 : min, already ? 0 : max)) return applyFromForm(key);
    } else if (link.dataset.amPriceMax) {
      if (setPrice(0, Number(link.dataset.amPriceMax))) return applyFromForm(key);
    }
    render(link.href, { key });
  });

  // "Show more lamps": append the next page instead of navigating.
  async function appendPage(href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set("section_id", sectionId);
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const doc = parser.parseFromString(await response.text(), "text/html");
    const items = [...doc.querySelectorAll("[data-am-grid] > li:not(.am-plp__promo-cell)")];
    document.querySelector("[data-am-grid]")?.append(...items);
    swap(doc, "[data-am-more]");
    return items;
  }

  // Remember how many pages are showing, so Back (or a reload) returns to the same place.
  const remember = (patch) => window.history.replaceState({ ...(window.history.state || {}), ...patch }, "");

  document.addEventListener("click", async (event) => {
    const more = event.target.closest("[data-am-load-more]");
    if (!more || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    more.setAttribute("aria-busy", "true");
    more.textContent = "Loading…";
    try {
      const page = Number(new URL(more.href, window.location.origin).searchParams.get("page")) || 2;
      const items = await appendPage(more.href);
      items[0]?.querySelector("a")?.focus({ preventScroll: true });
      remember({ amPages: page });
      window.history.scrollRestoration = "manual";
      window.AM?.announce?.(`${items.length} more lamps shown`);
    } catch {
      window.location.href = more.href;
    }
  });

  // Note the scroll position on the way to a product (and when leaving any other way).
  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-am-grid] a")) remember({ amScroll: window.scrollY });
  });
  window.addEventListener("pagehide", () => {
    if (window.history.state?.amPages > 1) remember({ amScroll: window.scrollY });
  });

  async function restorePages(state) {
    const pages = Number(state?.amPages) || 1;
    for (let page = 2; page <= pages; page += 1) {
      const more = document.querySelector("[data-am-load-more]");
      if (!more) break;
      try {
        await appendPage(more.href);
      } catch {
        break;
      }
    }
    if (pages > 1 && state?.amScroll) window.scrollTo(0, state.amScroll);
  }

  window.addEventListener("popstate", async (event) => {
    await render(window.location.href, { push: false, key: null });
    restorePages(event.state);
  });

  if (window.history.state?.amPages > 1) {
    window.history.scrollRestoration = "manual";
    restorePages(window.history.state);
  }
}
